import { useEffect, useMemo, useState } from 'react';
import {
  PortalDsAlert,
  PortalDsBadge,
  PortalDsButton,
  PortalDsEmptyState,
  PortalDsNoticeBar,
  portalDs,
} from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from 'src/components/ui/sheet';
import type { NormalizedMarket } from 'src/redux/survey/prediction-markets.endpoints';
import {
  formatCloseDate,
  formatProbability,
  formatVolume,
  MAX_ATTACHED_MARKETS,
  providerLabel,
} from './market-utils';

interface MarketSearchSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidates: NormalizedMarket[];
  partial: boolean;
  providerErrors: string[];
  isSearching: boolean;
  searchError?: string | null;
  onRetry: () => void;
  onAttach: (selected: NormalizedMarket[]) => Promise<void>;
  isAttaching: boolean;
  existingCount: number;
}

function selectionKey(market: NormalizedMarket): string {
  return `${market.provider}:${market.provider_market_id}`;
}

export function MarketSearchSheet({
  open,
  onOpenChange,
  candidates,
  partial,
  providerErrors,
  isSearching,
  searchError,
  onRetry,
  onAttach,
  isAttaching,
  existingCount,
}: MarketSearchSheetProps) {
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const remainingSlots = Math.max(0, MAX_ATTACHED_MARKETS - existingCount);

  useEffect(() => {
    if (isSearching) {
      setSelectedKeys([]);
    }
  }, [isSearching]);

  const selectedMarkets = useMemo(
    () => candidates.filter((candidate) => selectedKeys.includes(selectionKey(candidate))),
    [candidates, selectedKeys]
  );

  const toggleSelection = (market: NormalizedMarket) => {
    const key = selectionKey(market);
    if (selectedKeys.includes(key)) {
      setSelectedKeys(selectedKeys.filter((item) => item !== key));
      return;
    }
    if (selectedKeys.length >= remainingSlots) {
      return;
    }
    setSelectedKeys([...selectedKeys, key]);
  };

  const handleAttach = async() => {
    await onAttach(selectedMarkets);
    setSelectedKeys([]);
    onOpenChange(false);
  };

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
    >
      <SheetContent className="w-full overflow-y-auto bg-white sm:max-w-xl">
        <SheetHeader className={portalDs.sheetHeader}>
          <SheetTitle className={portalDs.sheetTitle}>
            Find related markets
          </SheetTitle>
          <SheetDescription className={portalDs.sheetDescription}>
            Select up to
            {' '}
            {remainingSlots}
            {' '}
            market
            {remainingSlots === 1 ? '' : 's'}
            {' '}
            to attach.
            Deselect one before choosing another once you reach the cap.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {isSearching && (
            <div
              className="flex items-center gap-3 py-2"
              role="status"
              aria-live="polite"
            >
              <div
                className="size-5 shrink-0 animate-spin rounded-full border-2 border-[#D9DBE3] border-t-[#2563EB]"
                aria-hidden="true"
              />
              <p className={portalDs.bodySm}>
                Searching prediction markets…
              </p>
            </div>
          )}

          {!isSearching && searchError && (
            <PortalDsAlert
              variant="danger"
              title={searchError}
            >
              <PortalDsButton
                className="mt-3"
                variant="secondary"
                size="sm"
                onClick={onRetry}
              >
                Retry search
              </PortalDsButton>
            </PortalDsAlert>
          )}

          {!isSearching && providerErrors.length > 0 && (
            <PortalDsNoticeBar>
              <div>
                {[...new Set(providerErrors)].map((error) => (
                  <p key={error}>
                    {error}
                  </p>
                ))}
              </div>
            </PortalDsNoticeBar>
          )}

          {!isSearching && partial && candidates.length > 0 && (
            <p className={portalDs.bodySm}>
              Fewer than 10 relevant markets were found. Showing what is available.
            </p>
          )}

          {!isSearching && !searchError && candidates.length === 0 && (
            <PortalDsEmptyState
              title="No live markets matched this survey"
              action={(
                <PortalDsButton
                  variant="secondary"
                  onClick={onRetry}
                >
                  Try again
                </PortalDsButton>
              )}
            />
          )}

          {!isSearching && candidates.map((candidate) => {
            const key = selectionKey(candidate);
            const isSelected = selectedKeys.includes(key);
            const isDisabled = !isSelected && selectedKeys.length >= remainingSlots;
            return (
              <button
                key={key}
                type="button"
                disabled={isDisabled}
                onClick={() => toggleSelection(candidate)}
                className={cn(
                  portalDs.card,
                  portalDs.cardPadding,
                  'w-full text-left transition',
                  isSelected && 'border-[#2563EB] bg-[#EFF6FF]',
                  isDisabled && 'bg-[#F7F8FA] opacity-60',
                  !isSelected && !isDisabled && 'hover:border-[#B8BBCA]'
                )}
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <PortalDsBadge variant="gray">
                    {providerLabel(candidate.provider)}
                  </PortalDsBadge>
                  <span className="text-[13.5px] font-semibold text-[#0D0F1A]">
                    {formatProbability(candidate.primary_probability)}
                  </span>
                </div>
                <h4 className={portalDs.headingH3}>
                  {candidate.title}
                </h4>
                {candidate.relevance_note && (
                  <p className={cn(portalDs.bodySm, 'mt-2')}>
                    {candidate.relevance_note}
                  </p>
                )}
                <div className={cn(portalDs.caption, 'mt-3 flex flex-wrap gap-3')}>
                  <span>
                    Closes
                    {formatCloseDate(candidate.close_time)}
                  </span>
                  <span>
                    Volume
                    {formatVolume(candidate.volume_usd)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        <SheetFooter className="mt-6">
          <PortalDsButton
            variant="accent"
            disabled={selectedMarkets.length === 0 || isAttaching || isSearching}
            onClick={() => {
              void handleAttach();
            }}
          >
            Attach selected markets
          </PortalDsButton>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
